// src/components/content/InteractiveDrill.js

import React, { useState } from 'react';
import { FaCheckCircle, FaTimesCircle, FaLightbulb, FaSpinner } from 'react-icons/fa';
import parse from 'html-react-parser';
import { InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import axiosInstance from '../../modules/api'; 
// ... (توابع کمکی unescapeHTML و parseOptions و DrillContentRenderer) ...
function unescapeHTML(escapedHTML) {
    if (typeof escapedHTML !== 'string') return '';
    const tempElement = document.createElement('textarea');
    tempElement.innerHTML = escapedHTML;
    return tempElement.value;
}
const parseOptions = {
    replace: (domNode) => {
        if (domNode.type === 'text' && domNode.data && domNode.data.includes('$')) {
            const parts = domNode.data.split(/(\$.*?\$)/g);
            if (parts.length <= 1) return null;
            
            return (
                <>{parts.filter(part => part.length > 0).map((part, index) => {
                    if (part.startsWith('$') && part.endsWith('$')) {
                        const math = part.slice(1, -1);
                        try {
                            return <InlineMath key={index} math={math} />;
                        } catch (error) {
                            return <span key={index} style={{ color: '#e74c3c', fontWeight: 'bold' }}>[خطای فرمول]</span>;
                        }
                    }
                    return <span key={index}>{part}</span>;
                })}</>
            );
        }
        return null;
    },
};
const DrillContentRenderer = ({ html }) => {
    if (!html) return null;
    const unescaped = unescapeHTML(html);
    return <>{parse(unescaped, parseOptions)}</>;
};


// ==============================================================================
// ۲. کامپوننت اصلی تمرین تعاملی
// ==============================================================================

const InteractiveDrill = ({ blockData }) => {
    
    // استخراج ایمن داده‌ها
    const drillContent = blockData.json_data;
    // ✅ چک امنیتی برای آرایه exercises (رفع خطای map)
    const exercises = drillContent?.exercises; 
    
    // ⚠️ توجه: اگر این بلوک (Drill Block) برای نمایش محتوای درسنامه استفاده شده باشد،
    // محتوای درسنامه در فیلد content_html داخل json_data قرار دارد.
    const lessonHtmlContent = drillContent?.content_html;
    
    const [answers, setAnswers] = useState({});
    const [results, setResults] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // 3. اگر فقط محتوای HTML درسنامه موجود است، آن را با ContentRenderer نمایش دهید.
    if (lessonHtmlContent && (!exercises || exercises.length === 0)) {
        return <DrillContentRenderer html={lessonHtmlContent} />;
    }
    
    // 4. خروج در صورت عدم وجود تمرینات (تنها در صورتی که محتوای درسی هم نداشته باشیم)
    if (!exercises || !Array.isArray(exercises) || exercises.length === 0) {
        return (
            <div className="drill-container">
                <p className="drill-message error">
                    تمرینات تعاملی برای این بلوک تعریف نشده‌اند.
                </p>
            </div>
        );
    }
    
    // ... (ادامه توابع handleSubmit و handleChange) ...

    const handleAnswerChange = (exerciseId, value) => {
        setAnswers(prev => ({
            ...prev,
            [exerciseId]: value,
        }));
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        setError(null);
        setResults(null);
        
        try {
            const payload = {
                content_block_id: blockData.id,
                answers: answers,
            };
            
            const response = await axiosInstance.post(`/core/student/drills/submit/`, payload);
            
            setResults(response.data.results);
        
        } catch (err) {
            setError("خطا در ارسال پاسخ. لطفاً دوباره تلاش کنید.");
            console.error("Error submitting drill:", err);
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <div className="drill-container">
            {/* عنوان و مقدمه Drill */}
            {drillContent.introduction_html && (
                <div className="drill-introduction">
                    <DrillContentRenderer html={drillContent.introduction_html} />
                </div>
            )}


            <div className="drill-items">
                {/* ✅ اکنون map به صورت ایمن اجرا می‌شود */}
                {exercises.map(exercise => { 
                    const result = results ? results[exercise.id] : null;
                    let inputClassName = '';
                    if (result) {
                        inputClassName = result.correct ? 'input-correct' : 'input-wrong';
                    }

                    return (
                        <div key={exercise.id} className="drill-item">
                            <label>
                                {/* نمایش متن سوال */}
                                <DrillContentRenderer html={exercise.question_html} />
                            </label>
                            <div className="answer-wrapper">
                                <input
                                    type="text"
                                    value={answers[exercise.id] || ''}
                                    onChange={(e) => handleAnswerChange(exercise.id, e.target.value)}
                                    className={inputClassName}
                                    disabled={!!results || isLoading}
                                />
                                {result && (
                                    <span className="feedback-icon">
                                        {result.correct ? (
                                            <FaCheckCircle className="correct" title="پاسخ صحیح" />
                                        ) : (
                                            <FaTimesCircle className="wrong" title="پاسخ اشتباه" />
                                        )}
                                    </span>
                                )}
                            </div>
                            {result && !result.correct && (
                                <div className="correct-answer-hint">
                                    <FaLightbulb />
                                    <span>پاسخ صحیح: {result.correct_answer}</span>
                                    {result.explanation_html && (
                                        <div className="explanation-html">
                                            <DrillContentRenderer html={result.explanation_html} />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {error && <p className="drill-message error">{error}</p>}
            
            {!results && (
                <button onClick={handleSubmit} disabled={isLoading} className="button button-primary drill-submit-btn">
                    {isLoading ? <FaSpinner className="spinner" /> : 'ثبت پاسخ‌ها و بررسی'}
                </button>
            )}

            {results && (
                <p className="drill-message success">ارزیابی انجام شد. می‌توانید نتایج را مشاهده کنید.</p>
            )}
        </div>
    );
};

export default InteractiveDrill;